/*
 * Static data layer.
 *
 * The site has no backend — this module emulates the old REST API by resolving
 * the same paths against the static content in `src/data/content.js`. Page
 * components keep using `fetcher(path)` and `api.post(...)` unchanged.
 *
 * Contact form submissions are sent to Web3Forms (https://web3forms.com),
 * a free static-friendly service that emails them to you. Put your access key
 * in `REACT_APP_WEB3FORMS_KEY` (frontend/.env) before going live.
 */
import { SITE, PROFILE, CASE_STUDIES, BLOGS } from "@/data/content";

// Link to the pre-generated resume PDF placed in `public/`.
export const RESUME_URL = "/Nishant_Gathwal_Resume.pdf";

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY || "";

const clone = (v) => JSON.parse(JSON.stringify(v));

function resolve(path) {
  const clean = path.split("?")[0].replace(/\/$/, "");

  if (clean === "" || clean === "/") return { message: "Portfolio is running" };
  if (clean === "/profile") return clone(PROFILE);
  if (clean === "/site") return clone(SITE);

  if (clean === "/case-studies") return clone(CASE_STUDIES);
  if (clean.startsWith("/case-studies/")) {
    const slug = clean.replace("/case-studies/", "");
    const found = CASE_STUDIES.find((c) => c.slug === slug);
    if (!found) {
      const err = new Error("Case study not found");
      err.response = { status: 404 };
      throw err;
    }
    return clone(found);
  }

  if (clean === "/blogs") {
    return clone([...BLOGS].sort((a, b) => (a.date < b.date ? 1 : -1)));
  }
  if (clean.startsWith("/blogs/")) {
    const slug = clean.replace("/blogs/", "");
    const found = BLOGS.find((b) => b.slug === slug);
    if (!found) {
      const err = new Error("Blog post not found");
      err.response = { status: 404 };
      throw err;
    }
    return clone(found);
  }

  const err = new Error(`Unknown path: ${path}`);
  err.response = { status: 404 };
  throw err;
}

export const fetcher = async (path) => resolve(path);

// Minimal axios-compatible shim so existing call sites keep working.
export const api = {
  get: async (path) => ({ data: resolve(path) }),
  post: async (path, payload) => {
    if (path.replace(/\/$/, "") === "/contact") {
      if (!WEB3FORMS_KEY) {
        throw new Error(
          "Contact form is not configured. Set REACT_APP_WEB3FORMS_KEY in frontend/.env."
        );
      }
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          name: payload.name,
          email: payload.email,
          subject: payload.subject || "New message from portfolio",
          message: payload.message,
          from_name: "nishantgathwal.com",
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to send message");
      }
      return { data: { id: data.data?.id || "ok", ...payload } };
    }
    throw new Error(`Unsupported POST: ${path}`);
  },
};

// Kept for backwards compatibility with any `${API}/...` references.
export const API = "";
